import React, { useEffect, useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, Trophy, ChartBar } from "@phosphor-icons/react";
import { STAGE_MAP, sourceLabel } from "@/lib/crm";

function money(n) {
  const v = Number(n) || 0;
  return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
}
const pct = (r) => `${Math.round((Number(r) || 0) * 100)}%`;

export default function CrmDashboard({ isOwner }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/crm/dashboard?days=${days}`);
        setData(d);
      } catch (e) { setErr(getErr(e)); }
    })();
  }, [days]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/crm/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "crm_leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) { toast.error(getErr(e)); }
  };

  if (err) return <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="crm-dashboard-error">{err}</div>;
  if (!data) return <div className="font-mono-label">Loading dashboard…</div>;

  const maxCount = Math.max(1, ...data.funnel.map((f) => f.count));
  const c = data.conversion;

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ChartBar size={16} />
          <span className="font-mono-label">Performance — last</span>
          <select data-testid="crm-dashboard-days" value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="h-8 border border-[#030712] bg-white px-2 text-xs">
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>12 months</option>
          </select>
          <span className="font-mono text-xs text-[#4B5563]">{data.total_leads} leads</span>
        </div>
        {isOwner && (
          <button onClick={exportCsv} data-testid="crm-export-csv-btn"
            className="inline-flex h-8 items-center gap-1.5 border border-[#030712] px-3 text-xs font-semibold hover:bg-[#F9FAFB]">
            <DownloadSimple size={14} /> Export CSV (all leads)
          </button>
        )}
      </div>

      {/* Conversion tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="crm-conversion-tiles">
        <div className="border border-[#E5E7EB] bg-white p-4">
          <div className="font-mono-label">Contact rate</div>
          <div className="mt-1 font-display text-2xl font-black">{pct(c.contact_rate)}</div>
        </div>
        <div className="border border-[#E5E7EB] bg-white p-4">
          <div className="font-mono-label">Quote rate</div>
          <div className="mt-1 font-display text-2xl font-black">{pct(c.quote_rate)}</div>
        </div>
        <div className="border border-[#E5E7EB] bg-white p-4">
          <div className="font-mono-label">Win rate</div>
          <div className="mt-1 font-display text-2xl font-black">{pct(c.win_rate)}</div>
          <div className="text-[10px] text-[#6B7280]">{c.won} won · {c.lost} lost</div>
        </div>
        <div className="border border-emerald-400 bg-white p-4">
          <div className="font-mono-label">Value won</div>
          <div className="mt-1 font-display text-2xl font-black text-emerald-700">{money(c.value_won)}</div>
        </div>
      </div>

      {/* Funnel */}
      <div className="border border-[#E5E7EB] bg-white p-4" data-testid="crm-funnel">
        <div className="font-mono-label mb-3">Pipeline funnel</div>
        <div className="space-y-2">
          {data.funnel.map((f) => (
            <div key={f.stage} className="flex items-center gap-2 text-xs">
              <div className="w-24 shrink-0 font-bold uppercase tracking-widest text-[10px]">{f.label}</div>
              <div className="h-5 min-w-[2px]" style={{
                width: `${Math.max(2, (f.count / maxCount) * 100)}%`,
                backgroundColor: STAGE_MAP[f.stage].color,
              }} />
              <div className="shrink-0 font-mono">{f.count} · {money(f.value)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* VA leaderboard */}
        <div className="border border-[#E5E7EB] bg-white" data-testid="crm-leaderboard">
          <div className="flex items-center gap-2 border-b border-[#E5E7EB] px-4 py-2 font-mono-label">
            <Trophy size={14} /> VP leaderboard (attribution)
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left font-mono uppercase text-[9px] tracking-widest text-[#4B5563]">
                <th className="px-3 py-2">VP</th><th className="px-3 py-2 text-right">Leads</th>
                <th className="px-3 py-2 text-right">Won</th><th className="px-3 py-2 text-right">Win %</th>
                <th className="px-3 py-2 text-right">Value won</th>
              </tr>
            </thead>
            <tbody>
              {data.leaderboard.map((r, i) => (
                <tr key={r.va_user_id} className="border-t border-[#F3F4F6]">
                  <td className="px-3 py-2 font-semibold">{i + 1}. {r.va_name || r.va_user_id}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.leads}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.won}</td>
                  <td className="px-3 py-2 text-right font-mono">{pct(r.win_rate)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{money(r.value_won)}</td>
                </tr>
              ))}
              {data.leaderboard.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-[#9CA3AF]">No attributed leads in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Source report */}
        <div className="border border-[#E5E7EB] bg-white" data-testid="crm-source-report">
          <div className="border-b border-[#E5E7EB] px-4 py-2 font-mono-label">Lead sources</div>
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left font-mono uppercase text-[9px] tracking-widest text-[#4B5563]">
                <th className="px-3 py-2">Source</th><th className="px-3 py-2 text-right">Leads</th>
                <th className="px-3 py-2 text-right">Won</th><th className="px-3 py-2 text-right">Win %</th>
                <th className="px-3 py-2 text-right">Value won</th>
              </tr>
            </thead>
            <tbody>
              {data.sources.map((r) => (
                <tr key={r.source} className="border-t border-[#F3F4F6]">
                  <td className="px-3 py-2 font-semibold">{sourceLabel(r.source)}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.leads}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.won}</td>
                  <td className="px-3 py-2 text-right font-mono">{pct(r.win_rate)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{money(r.value_won)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Aging */}
      <div className="border border-[#E5E7EB] bg-white" data-testid="crm-aging">
        <div className="border-b border-[#E5E7EB] px-4 py-2 font-mono-label">Pipeline aging (time in current stage)</div>
        <table className="w-full text-xs">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left font-mono uppercase text-[9px] tracking-widest text-[#4B5563]">
              <th className="px-3 py-2">Stage</th><th className="px-3 py-2 text-right">Open leads</th>
              <th className="px-3 py-2 text-right">Avg days</th><th className="px-3 py-2 text-right">Stuck &gt;7d</th>
            </tr>
          </thead>
          <tbody>
            {data.aging.map((r) => (
              <tr key={r.stage} className="border-t border-[#F3F4F6]">
                <td className="px-3 py-2 font-semibold">{r.label}</td>
                <td className="px-3 py-2 text-right font-mono">{r.count}</td>
                <td className="px-3 py-2 text-right font-mono">{r.avg_days}</td>
                <td className={`px-3 py-2 text-right font-mono ${r.stuck_7d > 0 ? "font-bold text-red-600" : ""}`}>{r.stuck_7d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
